import { imgproxyHealthyAtom, proxyMediaUrl, reportImgproxyFailure } from '@/atoms/imgproxy'
import { cn } from '@/lib/utils'
import { markAsSuccess, tryNextUrl } from '@/services/blossom.service'
import { TImetaInfo } from '@/types'
import { decode } from 'blurhash'
import { getDefaultStore } from 'jotai'
import { thumbHashToDataURL } from 'thumbhash'
import { HugeiconsIcon } from '@hugeicons/react'
import { ImageNotFound01Icon } from '@hugeicons/core-free-icons'
import { HTMLAttributes, useEffect, useMemo, useRef } from 'react'
import { getHashFromURL } from '@nostr/tools/nipb7'
import { useAsync } from 'react-use'

const store = getDefaultStore()

export default function Image({
  imageUrl,
  imagePubkey,
  imeta,
  alt,
  className = '',
  classNames = {},
  hideIfError = false,
  placeholder = <HugeiconsIcon icon={ImageNotFound01Icon} />,
  imgproxyModifiers,
  onChosenUrl,
  onFailure,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  classNames?: {
    wrapper?: string
    placeholder?: string
  }
  imageUrl?: string
  imagePubkey?: string
  imeta?: TImetaInfo
  alt?: string
  hideIfError?: boolean
  placeholder?: React.ReactNode
  imgproxyModifiers?: string
  onChosenUrl?: (url: string) => void
  onFailure?: () => void
}) {
  const isBlossomUrl = useMemo(() => {
    if (!imagePubkey) return false
    if (!imageUrl?.startsWith('http')) return false

    try {
      return !!getHashFromURL(imageUrl)
    } catch {
      return false
    }
  }, [imageUrl])
  const blossomUrlRef = useRef<string>(imageUrl || '')

  const { value, error, loading } = useAsync(async () => {
    if (!imageUrl) {
      if (typeof placeholder === 'string') {
        onChosenUrl?.(placeholder)
        return placeholder
      } else {
        throw new Error('<no url>')
      }
    }

    if (!imageUrl.startsWith('http')) {
      onChosenUrl?.(imageUrl)
      return imageUrl
    }

    const tried = new Set<string>()
    const active = true

    let finalUrl: string | undefined

    if (!isBlossomUrl) {
      const proxyResult = await tryWithImgproxy(imageUrl)
      if (proxyResult) {
        finalUrl = proxyResult
      } else if (await tryUrl(imageUrl)) {
        finalUrl = imageUrl
      }
    } else {
      try {
        tried.add(new URL(imageUrl).hostname)
      } catch {
        throw new Error('<url is invalid>')
      }
      blossomUrlRef.current = imageUrl

      const proxyResult = await tryWithImgproxy(imageUrl)
      if (proxyResult) {
        finalUrl = proxyResult
      } else if (await tryUrl(imageUrl)) {
        finalUrl = imageUrl
      } else {
        while (true) {
          const nextUrl = await tryNextUrl(imageUrl, imagePubkey!, tried)
          if (!nextUrl) break
          blossomUrlRef.current = nextUrl
          const nextProxy = await tryWithImgproxy(nextUrl)
          if (nextProxy) {
            finalUrl = nextProxy
            break
          }
          if (await tryUrl(nextUrl)) {
            finalUrl = nextUrl
            break
          }
        }
      }
    }

    if (!active) return

    if (finalUrl) {
      markAsSuccess(finalUrl, blossomUrlRef.current)
      onChosenUrl?.(finalUrl)
      return finalUrl
    } else {
      onFailure?.()
      throw new Error('<url and fallbacks not working>')
    }

    async function tryUrl(urlToTry: string): Promise<boolean> {
      return new Promise((resolve) => {
        const img = new window.Image()
        img.onload = () => resolve(true)
        img.onerror = () => resolve(false)
        img.src = urlToTry
      })
    }

    async function tryWithImgproxy(target: string): Promise<string | null> {
      const healthy = await store.get(imgproxyHealthyAtom)
      if (!healthy) return null
      const proxyUrl = await proxyMediaUrl(target, imgproxyModifiers)
      if (proxyUrl === target) return null
      if (!active) return null
      if (await tryUrl(proxyUrl)) return proxyUrl
      if (!active) return null
      const stillHealthy = await store.get(imgproxyHealthyAtom)
      if (stillHealthy) {
        reportImgproxyFailure()
      } else if (tried.size > 1) {
        tried.clear()
        try {
          tried.add(new URL(target).hostname)
        } catch {
          /***/
        }
      }
      return null
    }
  }, [imageUrl, imagePubkey, imgproxyModifiers])

  if (hideIfError && error) return null

  if (value) {
    return (
      <div className={cn('relative overflow-hidden', classNames.wrapper)} {...props}>
        <img
          key={value}
          src={value}
          alt={alt}
          decoding="async"
          draggable={false}
          className={cn(
            'object-cover rounded-sm w-full h-full transition-opacity pointer-events-none opacity-100',
            className
          )}
          width={imeta?.dim?.width}
          height={imeta?.dim?.height}
        />
      </div>
    )
  }

  if (error || (loading && !imeta?.thumbHash && !imeta?.blurHash)) {
    if (typeof placeholder === 'string')
      return (
        <div className={cn('relative overflow-hidden', classNames.wrapper)} {...props}>
          <img
            src={placeholder}
            className={cn('object-cover rounded-sm w-full h-full transition-opacity', className)}
          />
        </div>
      )

    return (
      <div className={cn('relative overflow-hidden', classNames.wrapper)} {...props}>
        <div
          className={cn(
            'object-cover flex flex-col items-center justify-center w-full h-full bg-muted',
            className,
            classNames.placeholder
          )}
        >
          {placeholder}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('relative overflow-hidden', classNames.wrapper)} {...props}>
      {loading && (
        <div className="absolute inset-0">
          {imeta?.thumbHash ? (
            <ThumbHashPlaceholder
              thumbHash={imeta.thumbHash}
              className="absolute inset-0 transition-opacity rounded-sm"
            />
          ) : imeta?.blurHash ? (
            <BlurHashCanvas
              blurHash={imeta.blurHash}
              className="absolute inset-0 transition-opacity rounded-sm"
            />
          ) : (
            <div className="absolute inset-0 transition-opacity rounded-sm bg-muted flex items-center justify-center p-2">
              <span className="text-xs text-muted-foreground break-all text-center line-clamp-3 font-mono select-all">
                {imageUrl}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const blurHashWidth = 32
const blurHashHeight = 32
function BlurHashCanvas({ blurHash, className = '' }: { blurHash: string; className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const pixels = useMemo(() => {
    if (!blurHash) return null
    try {
      return decode(blurHash, blurHashWidth, blurHashHeight)
    } catch (error) {
      console.warn('Failed to decode blurhash:', error)
      return null
    }
  }, [blurHash])

  useEffect(() => {
    if (!pixels || !canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const imageData = ctx.createImageData(blurHashWidth, blurHashHeight)
    imageData.data.set(pixels)
    ctx.putImageData(imageData, 0, 0)
  }, [pixels])

  if (!blurHash) return null

  return (
    <canvas
      ref={canvasRef}
      width={blurHashWidth}
      height={blurHashHeight}
      className={cn('w-full h-full object-cover rounded-sm', className)}
      style={{
        imageRendering: 'auto',
        filter: 'blur(0.5px)'
      }}
    />
  )
}

function ThumbHashPlaceholder({
  thumbHash,
  className = ''
}: {
  thumbHash: Uint8Array
  className?: string
}) {
  const dataUrl = useMemo(() => {
    if (!thumbHash) return null
    try {
      return thumbHashToDataURL(thumbHash)
    } catch (error) {
      console.warn('failed to decode thumbhash:', error)
      return null
    }
  }, [thumbHash])

  if (!dataUrl) return null

  return (
    <div
      className={cn('w-full h-full object-cover rounded-sm', className)}
      style={{
        backgroundImage: `url(${dataUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'blur(1px)'
      }}
    />
  )
}
